import {
  LatLng,
  distanceTo,
  getBoundingBox,
  LatLon,
  insideCircle,
  insideBoundingBox,
} from "geolocation-utils";

import { Utils } from "utils";
import { ICircle } from "types";

import {
  findCoordinateAt,
  distanceToCoordinate,
  calculateDistanceFor,
  isPointsInsideCircles,
} from "./helpers";

export class Road {
  private _coordinates: LatLng[] = [];

  public static fromLatLng(coordinates: LatLng[]): Road {
    const road = new Road();
    road.coordinates = coordinates;
    return road;
  }

  public static fromPoints(points: number[][]): Road {
    const road = new Road();
    road.coordinates = points.map(Utils.Graphhopper.convertToLatLng);
    return road;
  }

  public get coordinates() {
    return this._coordinates;
  }

  public get time() {
    const averageBikeSpeed = 50; // 50 km/h - average bike speed;
    return this.getDistance("km") / averageBikeSpeed; // hours
  }

  public set coordinates(coordiantes: LatLng[]) {
    this._coordinates = coordiantes;
  }

  public getDistance(measure: "km" | "m") {
    const distance = calculateDistanceFor(this._coordinates);
    return measure === "km" ? Number((distance / 1000).toFixed(2)) : distance;
  }

  public get coordinatesLatLon() {
    return this._coordinates.map((coord) => ({
      lat: coord.lat,
      lon: coord.lng,
    }));
  }

  public get boundingBox() {
    const bbox = getBoundingBox(
      this._coordinates.map((i) => ({ lat: i.lat, lon: i.lng })),
      0
    );
    return bbox;
  }

  public get bouindingBoxCenter() {
    const bbox = this.boundingBox;

    const topLeft = bbox.topLeft as unknown as LatLon;
    const bottomRight = bbox.bottomRight as unknown as LatLon;

    const lat1 = topLeft.lat;
    const lng1 = topLeft.lon;
    const lat2 = bottomRight.lat;
    const lng2 = bottomRight.lon;

    const centerLat = (lat1 + lat2) / 2;
    const centerLng = (lng1 + lng2) / 2;

    const middlePoint = { lat: centerLat, lng: centerLng };

    return middlePoint;
  }

  public getTime(variant: "hours" | "minutes") {
    const averageBikeSpeed = 50; // 50 km/h - average bike speed;
    const hours = this.getDistance("km") / averageBikeSpeed; // hours
    return variant === "hours" ? hours : hours * 60;
  }

  public getBounds() {
    const bbox = this.boundingBox as unknown as {
      topLeft: LatLon;
      bottomRight: LatLon;
    };

    const topLeft = { lat: bbox.topLeft.lat, lng: bbox.topLeft.lon };
    const bottomRight = {
      lat: bbox.bottomRight.lat,
      lng: bbox.bottomRight.lon,
    };
    return Utils.Helpers.convertBBoxToBounds({ topLeft, bottomRight });
  }

  public getBoundsForCountryCode(code: string) {
    const { bounds } = Utils.Helpers.bouindingFor(code);
    return bounds;
  }

  public include(coordinate: LatLng) {
    return (
      this._coordinates.filter(
        (coords) =>
          coords.lat === coordinate.lat && coords.lng === coordinate.lng
      ).length > 0
    );
  }

  public isInBoundingBox(coordinate: LatLng) {
    const location = Utils.Coordinates.convert.latLng.toLatLon(coordinate);
    const bbox = this.boundingBox;
    return insideBoundingBox(location, bbox);
  }

  public coordinatesAlong(
    coordinates: LatLng[],
    searchRadius: number,
    gaps: [number, number] = [0, 0]
  ) {
    const roadCircles = this.circles({ radius: searchRadius, gaps });

    return coordinates.filter((coord) => {
      const isInside = roadCircles.filter((circle) =>
        insideCircle(coord, { lat: circle.lat, lng: circle.lng }, circle.radius)
      );
      return isInside.length > 0;
    });
  }

  public circles(props: { radius: number; gaps: [number, number] }) {
    const { radius, gaps } = props;
    const [startGap, finishGap] = gaps;

    const radiusInMeters = radius * 1000;
    const startGapInMeters = startGap * 1000;
    const finishGapInMeters = finishGap * 1000;

    let coords = this._coordinates;
    const distance = this.getDistance("km");

    const circles: ICircle[] = [];

    const getFullRoadCircle = () => {
      const center = this.bouindingBoxCenter;
      const distanceBetween = distanceTo(coords[0], coords[coords.length - 1]);
      return { ...center, radius: distanceBetween / 2 };
    };

    if (startGap > 0 || finishGap > 0) {
      coords = coords.filter((coord) => {
        const distanceToStart = distanceToCoordinate(coord, coords);
        const distanceToFinish = distanceToCoordinate(
          coord,
          [...coords].reverse()
        );

        return (
          distanceToStart >= startGapInMeters &&
          distanceToFinish >= finishGapInMeters
        );
      });
    }

    const firstCirlceCenter = findCoordinateAt(radiusInMeters, coords, radius);
    const lastCircleCenter = findCoordinateAt(
      radiusInMeters,
      [...coords].reverse(),
      radius
    );

    if (radius > distance + startGap + finishGap * 2) {
      const circle = getFullRoadCircle();
      return [circle];
    }

    if (firstCirlceCenter) {
      circles.push({ ...firstCirlceCenter, radius: radiusInMeters });
    }

    if (firstCirlceCenter && lastCircleCenter) {
      const insideCircles = [
        { ...firstCirlceCenter, radius: radiusInMeters },
        { ...lastCircleCenter, radius: radiusInMeters },
      ];
      const points = [coords[0], coords[coords.length - 1]];
      const isInside = isPointsInsideCircles(insideCircles, points);

      if (isInside) {
        const circle = getFullRoadCircle();
        return [circle];
      }

      const lastCircleDistance = distanceToCoordinate(lastCircleCenter, coords);
      // const firstCircleDistance = distanceToCoordinate(
      //   firstCirlceCenter,
      //   coords
      // );

      coords.forEach((coord) => {
        const prevCircle = circles[circles.length - 1];
        const distanceFromStart = distanceToCoordinate(coord, coords);
        const prevDistanceFromStart = distanceToCoordinate(prevCircle, coords);

        const difference = distanceFromStart - prevDistanceFromStart;

        if (
          difference >= radiusInMeters &&
          distanceFromStart < lastCircleDistance
        ) {
          circles.push({ ...coord, radius: radiusInMeters });
        }
      });
    }

    if (lastCircleCenter) {
      circles.push({ ...lastCircleCenter, radius: radiusInMeters });
    }

    return circles;
  }

  public findClosestPointsOn(coordinates: LatLng[]) {
    const roadCoords = this._coordinates;
    const [start, finish] = [roadCoords[0], roadCoords[roadCoords.length - 1]];

    const distancesToStart = coordinates.map((c) => distanceTo(start, c));
    const minDistanceToStart = Math.min(...distancesToStart);
    const startIndex = distancesToStart.indexOf(minDistanceToStart);

    const fromStartSlice = coordinates.slice(
      startIndex,
      coordinates.length - 1
    );
    const distancesToFinish = fromStartSlice.map((c) => distanceTo(finish, c));
    const minDistanceToFinish = Math.min(...distancesToFinish);
    const finishIndex = distancesToFinish.indexOf(minDistanceToFinish);

    const oldFinishIndex = finishIndex + startIndex;

    return {
      indexes: [startIndex, oldFinishIndex],
      distances: [minDistanceToStart, minDistanceToFinish],
    };
  }

  public findIndexOf(coordinate?: LatLng, order: "first" | "last" = "first") {
    const isSame = (item: LatLng) =>
      Utils.Coordinates.isEqualLatLng(item, coordinate);

    if (coordinate) {
      return order === "first"
        ? this.coordinates.findIndex(isSame)
        : this.coordinates.findLastIndex(isSame);
    }

    return -1;
  }
}
